import React, { useEffect, useState } from 'react';
import MultiSelect from '../ui/MultiSelect';
import { certificationsApi } from '../../services/certifications';

interface ProfileData {
  fullName: string;
  phone: string;
  profileImage: File | null;
  profileImageUrl?: string;
  businessName: string;
  licenseNumber: string;
  yearsOfExperience: number;
  bio: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  website: string;
  specialties: string[];
  services: string[];
  treatmentCategories: string[];
  pricingRange: string;
  virtualConsultations: boolean;
  education: Array<{
    degree: string[];
    institution: string;
    year: string;
  }>;
  certifications: Array<{
    name: string[];
    issuer: string;
    year: string;
  }>;
  affiliations: string[];
  insuranceAccepted: string[];
  languagesSpoken: string[];
}

interface Step4EducationProps {
  data: ProfileData;
  updateData: (updates: Partial<ProfileData>) => void;
}

const Step4Education: React.FC<Step4EducationProps> = ({ data, updateData }) => {
  const degreeOptions = ['Bachelor', 'Masters', 'Doctorate', 'Doctor'];
  const [certificationOptions, setCertificationOptions] = useState<string[]>([]);
  const [loadingCertifications, setLoadingCertifications] = useState(false);
  const [certificationError, setCertificationError] = useState<string | null>(null);

  // Fetch certification options on mount
  useEffect(() => {
    const fetchCertifications = async () => {
      setLoadingCertifications(true);
      setCertificationError(null);
      try {
        const certifications = await certificationsApi.getCertificationNames();
        setCertificationOptions(certifications);
      } catch (error) {
        console.error('Failed to fetch certifications:', error);
        setCertificationError('Failed to load certifications. Using defaults.');
        // Fallback to some common certifications
        setCertificationOptions(['ABPS', 'ABCS', 'AACS', 'AAD', 'ASPS', 'BLS', 'ACLS']);
      } finally {
        setLoadingCertifications(false);
      }
    };

    fetchCertifications();
  }, []);

  const addEducation = () => {
    updateData({
      education: [...data.education, { degree: [], institution: '', year: '' }],
    });
  };

  const updateEducation = (index: number, field: string, value: string | string[]) => {
    const updated = [...data.education];
    updated[index] = { ...updated[index], [field]: value };
    updateData({ education: updated });
  };

  const removeEducation = (index: number) => {
    updateData({
      education: data.education.filter((_, i) => i !== index),
    });
  };

  const addCertification = () => {
    updateData({
      certifications: [...data.certifications, { name: [], issuer: '', year: '' }],
    });
  };

  const updateCertification = (index: number, field: string, value: string | string[]) => {
    const updated = [...data.certifications];
    updated[index] = { ...updated[index], [field]: value };
    updateData({ certifications: updated });
  };

  const removeCertification = (index: number) => {
    updateData({
      certifications: data.certifications.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="font-['Ovo',_serif] text-[22px] sm:text-[26px] lg:text-[28px] text-[#1f1f1f] mb-4 sm:mb-6">
        Education & Credentials
      </h2>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
            Education
          </label>
          <button
            onClick={addEducation}
            className="text-[#c6a664] hover:text-[#b39754] font-['Open_Sans'] text-sm font-semibold min-h-[44px] px-2"
          >
            + Add Education
          </button>
        </div>

        {data.education.map((edu, index) => (
          <div key={index} className="mb-4 p-3 sm:p-4 border border-[#e5e5ea] rounded">
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              <MultiSelect
                options={degreeOptions}
                selected={edu.degree}
                onChange={(selectedDegrees) => updateEducation(index, 'degree', selectedDegrees)}
                placeholder="Select Degree(s)"
                className="w-full"
              />
              <input
                type="text"
                value={edu.institution}
                onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                className="px-3 py-3 sm:py-2 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
                placeholder="Institution"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={edu.year}
                  onChange={(e) => updateEducation(index, 'year', e.target.value)}
                  className="flex-1 px-3 py-3 sm:py-2 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
                  placeholder="Year"
                />
                <button
                  onClick={() => removeEducation(index)}
                  className="px-3 py-2 min-h-[44px] text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="font-['Open_Sans'] text-sm font-semibold text-[#1f1f1f]">
            Certifications
          </label>
          <button
            onClick={addCertification}
            className="text-[#c6a664] hover:text-[#b39754] font-['Open_Sans'] text-sm font-semibold min-h-[44px] px-2"
          >
            + Add Certification
          </button>
        </div>

        {data.certifications.map((cert, index) => (
          <div key={index} className="mb-4 p-3 sm:p-4 border border-[#e5e5ea] rounded">
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              <div>
                <MultiSelect
                  options={certificationOptions}
                  selected={cert.name}
                  onChange={(selectedCertifications) =>
                    updateCertification(index, 'name', selectedCertifications)
                  }
                  placeholder={
                    loadingCertifications ? 'Loading certifications...' : 'Select Certification(s)'
                  }
                  className="w-full"
                />
                {certificationError && index === 0 && (
                  <p className="text-xs text-orange-600 mt-1">{certificationError}</p>
                )}
              </div>
              <input
                type="text"
                value={cert.issuer}
                onChange={(e) => updateCertification(index, 'issuer', e.target.value)}
                className="px-3 py-3 sm:py-2 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
                placeholder="Issuing Organization"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cert.year}
                  onChange={(e) => updateCertification(index, 'year', e.target.value)}
                  className="flex-1 px-3 py-3 sm:py-2 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
                  placeholder="Year"
                />
                <button
                  onClick={() => removeCertification(index)}
                  className="px-3 py-2 min-h-[44px] text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Step4Education;
